import {
  ReactNode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import useWindowSize from 'react-use/lib/useWindowSize';
import type { NavigateFunction } from 'react-router-dom';
import {
  useReactRouterLoction as useLocation,
  useReactRouterNavigate as useBrowserNavigate,
  useNavigate,
} from 'views/Multitab/Router';
import { useMultitabReady, useMultitabState, useTabOperations } from 'views/Multitab/state';
import { Button, ConfigProvider, message, Modal, Spin } from 'antd';
import { useTranslate } from '@tolgee/react';
import { decode } from 'jsonwebtoken-esm';
import { authorizationEvents, useAuthorization } from 'components/Authorization';
import { useRouteAccessProps } from 'hooks/business/useNewPermisson';
import { getLeYanBotHost } from 'services/api/leyanBotHost';
import { logger } from 'services/logger';
import { getUnionId } from 'utils/getUnionId';
import logout from 'utils/logout';
import { createAgentApi } from 'services/agent/api';
import { AgentService, createAgentService } from 'services/agent/service';
import { Presentation } from 'services/agent/model';
import { useAgentAccess } from './access';
import { AgentContext, AgentContextValue } from './context';
import {
  AGENT_PAGES,
  canNavigate,
  navigationArrived,
  navigationPath,
  parseNavigation,
  workspaceForAgentLocation,
} from './navigation';
import { parseChoice } from 'services/agent/choice';
import './style.css';
import { parsePanelAction, isUnsupportedDestination } from './panels';
import { parseStoreAuth, STORE_AUTH_PLATFORMS, withStoreAuthPlatform } from './store-auth';
import { usePanelPresentation } from './use-panel-presentation';
import { collectUnreadKinds, completedUnreadSessionId } from 'services/agent/session-unread';
import {
  readAgentSelection,
  writeAgentSelection,
  type AgentSelectionIdentity,
} from 'services/agent/selection';
import { createAgentPageContextRegistry } from 'services/agent/page-context';
import { AgentPageReadersContext } from './page-context';
import agentSegments from './assets/agent-segments.svg';
import { MousePointer2 } from 'lucide-react';
import { navigateAgentPage } from 'services/agent/page-navigation';
import {
  createPagePointer,
  PAGE_POINTER_MOVE_MS,
  type PagePointerSnapshot,
} from 'services/agent/page-pointer';
import { createAgentPageTask } from 'services/agent/page-task';
import { agentPageTaskPrompt, type AgentPageTask } from 'services/agent/page-task-protocol';

const ChatView = lazy(() => import('./chat-view'));

function trackPageTask(event: string, data: unknown) {
  const allowed = new Set([
    'reason', 'scope', 'status', 'action', 'actionId', 'taskId', 'step', 'phase',
    'completedSteps', 'modelCalls', 'durationMs', 'firstActionMs', 'observeMs',
    'modelMs', 'actionMs', 'progressCount', 'trigger', 'attempt', 'attempts',
  ]);
  const fields: Record<string, string> = {};
  if (data && typeof data === 'object') {
    for (const [key, value] of Object.entries(data)) {
      if (allowed.has(key) && (typeof value === 'string' || typeof value === 'number'))
        fields[key] = String(value);
    }
  }
  logger.info(event, fields);
}

type PendingPanelAction = {
  payload: unknown;
  sessionId: string;
  path: string;
};

function removePendingPanelAction(actions: Record<string, PendingPanelAction>, sessionId: string) {
  const next = { ...actions };
  delete next[sessionId];
  return next;
}

function pageTaskForUndeclaredPanel(
  raw: unknown, request: string | undefined,
): AgentPageTask | undefined {
  if (!isUnsupportedDestination(raw)) return;
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const widget = value.widgets[0];
    if (!widget || typeof widget !== 'object' || Array.isArray(widget)) return;
    // 页面导航只接受客户端登记目标；页面模型兜底仅用于未登记表单。
    if (widget.type !== 'workspace_panel') return;
    const allowedKeys = ['type', 'id', 'panel', 'params'];
    if (Object.keys(widget).some((key) => !allowedKeys.includes(key))) return;
    if (typeof widget.id !== 'string' || !widget.id.trim() || widget.id.length > 64) return;
    const target = widget.panel;
    if (typeof target !== 'string' || !target.trim() || target.length > 128) return;
    const goal = request?.trim().slice(0, 2000) || target.replace(/[_-]/g, ' ');
    return {
      id: widget.id,
      goal: `Use visible page controls to fulfill this request: ${goal}. Open the relevant page or form and stop before submitting or saving.`,
      handoff: {
        facts: [`Undeclared destination: ${target}`],
        remaining: ['Find and open the requested page or form through visible controls'],
        completion: 'The requested page or form is visible',
        scope: 'workspace',
        budget: { maxSteps: 40, timeoutMs: 120000 },
      },
    };
  } catch {
    return;
  }
}

function quickStoreAuthorizationPath(payload: unknown, path: string): string | undefined {
  const storeAuth = parseStoreAuth(payload);
  if (!storeAuth || storeAuth.storeType || storeAuth.region || storeAuth.departmentIds?.length)
    return;
  return storeAuth.platform ? `${path}?tabName=${storeAuth.platform.toUpperCase()}` : path;
}

export function AgentProvider({ children }: { children: ReactNode }) {
  const allowed = useAgentAccess();
  const [pageReaders] = useState(() => createAgentPageContextRegistry(logger.info));
  const auth = useAuthorization<Oversea.Token.Payload>();
  const { t } = useTranslate();
  const location = useLocation();
  const nav = useNavigate() as NavigateFunction;
  const { activeKey, list: tabs } = useMultitabState();
  const { addTab, updateTab } = useTabOperations();
  const multitabReady = useMultitabReady();
  const browserNavigate = useBrowserNavigate();
  const pathname = activeKey || location.pathname;
  const search = activeKey ? tabs.find((tab) => tab.path === activeKey)?.search || '' : location.search;
  useEffect(() => {
    if (
      !allowed ||
      !multitabReady ||
      !/^\/agent(?:\/[A-Za-z0-9_-]{1,128})?\/?$/.test(location.pathname)
    )
      return;
    addTab({ path: location.pathname, search: location.search });
    browserNavigate('/workbench/conversation', { replace: true });
    logger.info('agent.tab.opened', { source: 'direct-link' });
  }, [allowed, multitabReady, location.pathname, location.search, addTab, browserNavigate]);
  const access = useRouteAccessProps();
  const fullPage = /^\/agent(?:\/|$)/.test(pathname);
  const [mode, setMode] = useState<Presentation | null>(null);
  const [returnSource, setReturnSource] = useState<{
    path: string;
    search: string;
    mode: 'sidebar' | 'floating-window';
  } | null>(null);
  const previousFullPage = useRef(fullPage);
  const agentInitiatedNavigation = useRef(false);
  useEffect(() => {
    if (allowed && previousFullPage.current && !fullPage && agentInitiatedNavigation.current) {
      setMode('sidebar');
      logger.info('agent.sidebar.opened', { source: 'full-page-navigation' });
    }
    if (previousFullPage.current && !fullPage) agentInitiatedNavigation.current = false;
    if (previousFullPage.current && !fullPage) setReturnSource(null);
    previousFullPage.current = fullPage;
  }, [allowed, fullPage]);
  const [selectedSessionId, setSelectedSessionId] = useState<string>();
  const { activePanel, panelPlacement, businessPanelOpen, openPanel, closePanel } =
    usePanelPresentation(fullPage, pathname, selectedSessionId);
  const [businessContainer, setBusinessContainer] = useState<HTMLDivElement | null>(null);
  const [platformChoices, setPlatformChoices] = useState<Record<string, PendingPanelAction>>({});
  const [pageContainer, setPageContainer] = useState<HTMLDivElement | null>(null);
  const [sidebarContainer, setSidebarContainer] = useState<HTMLElement | null>(null);
  const [floatContainer, setFloatContainer] = useState<HTMLDivElement | null>(null);
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [customSidebarWidth, setSidebarWidth] = useState<number>();
  const sidebarWidth = customSidebarWidth ?? Math.max(300, Math.min(380, windowWidth * 0.25));
  const [service, setService] = useState<AgentService>();
  const [agentStatus, setAgentStatus] = useState({ thinking: false, unread: false });
  const [pageVisible, setPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  );
  const [floatSize, setFloatSize] = useState<{ width: number; height: number }>();
  const resize = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
    left: number;
    top: number;
  }>();
  const [position, setPosition] = useState<{ x: number; y: number }>();
  // Keep the dragged launcher anchored to the bottom-right across viewport changes.
  const [launcherPosition, setLauncherPosition] = useState<{ right: number; bottom: number }>();
  const launcherDrag = useRef<{
    pointerId: number;
    x: number;
    y: number;
    left: number;
    top: number;
    width: number;
    height: number;
  }>();
  const suppressLauncherClick = useRef(false);
  useEffect(() => {
    const constrainFloatingWindow = () => {
      const floatingRect = floatContainer?.parentElement?.getBoundingClientRect();
      if (floatingRect) {
        setPosition({
          x: Math.max(8, Math.min(window.innerWidth - floatingRect.width - 8, floatingRect.left)),
          y: Math.max(8, Math.min(window.innerHeight - floatingRect.height - 8, floatingRect.top)),
        });
        setFloatSize((value) =>
          value
            ? {
                width: Math.min(value.width, window.innerWidth - 24),
                height: Math.min(value.height, window.innerHeight - 24),
              }
            : value,
        );
      }
    };
    window.addEventListener('resize', constrainFloatingWindow);
    return () => window.removeEventListener('resize', constrainFloatingWindow);
  }, [floatContainer]);
  const drag = useRef<{ x: number; y: number; left: number; top: number }>();
  const current = useRef({
    allowed,
    auth,
    nav,
    access,
    fullPage,
    mode,
    pathname,
    search,
    activeKey,
    updateTab,
    activePanel,
    t,
    pageVisible,
  });
  current.current = {
    allowed,
    auth,
    nav,
    access,
    fullPage,
    mode,
    pathname,
    search,
    activeKey,
    updateTab,
    activePanel,
    t,
    pageVisible,
  };
  useEffect(() => {
    const updateVisibility = () =>
      setPageVisible(typeof document === 'undefined' || document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', updateVisibility);
    return () => document.removeEventListener('visibilitychange', updateVisibility);
  }, []);
  const activeService = useRef<AgentService>();
  const pageTaskAbort = useRef<AbortController>();
  const pageTaskExecutor = useRef<(task: AgentPageTask, sessionId: string) => Promise<boolean>>();
  const knownNavigationExecutor = useRef<(destination: string, sessionId: string) => Promise<boolean>>();
  const navigationDestinations = useRef(new Map<string, { actionId: string; path: string }>());
  const knownPanelExecutor = useRef<(
    panel: NonNullable<ReturnType<typeof parsePanelAction>>, payload: unknown,
    path: string, sessionId: string,
  ) => Promise<boolean>>();
  const panelEntryExecutor = useRef<(
    selector: string,
    activate: (entry: HTMLElement) => void | Promise<void>,
    sessionId: string,
  ) => Promise<boolean>>();
  const [pointerSnapshot, setPointerSnapshot] = useState<PagePointerSnapshot | null>(null);
  const deferredNavigation = useRef(new Map<string, unknown>());
  const stayOnHome = useRef(false);
  const previousPathname = useRef(pathname);
  const authUserId = auth.payload?.id;
  const authOrgId = auth.payload?.orgId;
  const selectionIdentity = useMemo<AgentSelectionIdentity | undefined>(
    () =>
      authUserId !== undefined && authOrgId !== undefined
        ? { orgId: String(authOrgId), userId: String(authUserId) }
        : undefined,
    [authOrgId, authUserId],
  );
  const key = selectionIdentity
    ? `${selectionIdentity.orgId}:${selectionIdentity.userId}`
    : '';
  const pendingPlatform = selectedSessionId ? platformChoices[selectedSessionId] : undefined;
  const platformChoice =
    pendingPlatform && (fullPage || pathname === pendingPlatform.path)
      ? pendingPlatform
      : undefined;
  const navigate = useCallback(
    async (payload: unknown, sessionId: string): Promise<boolean> => {
      if (!payload || parseChoice(payload)) return false;
      const before = current.current;
      if (!before.allowed) return false;
      if (
        !before.pageVisible ||
        (!before.fullPage && !before.mode) ||
        activeService.current?.getSnapshot().selectedId !== sessionId
      ) {
        deferredNavigation.current.set(sessionId, payload);
        logger.info('agent.navigation.deferred', { sessionId, reason: 'surface_hidden' });
        return false;
      }
      const panel = parsePanelAction(payload);
      const action = panel
        ? { id: panel.id, workspace_id: panel.workspaceId }
        : parseNavigation(payload);
      if (!action) {
        const messages = activeService.current?.getSnapshot().messages[sessionId] || [];
        const request = [...messages].reverse().find((item) => item.role === 'user');
        const task = pageTaskForUndeclaredPanel(payload, request?.inputText || request?.content);
        if (task) {
          logger.info('agent.navigation.page_task', { kind: 'undeclared' });
          return pageTaskExecutor.current?.(task, sessionId) ?? false;
        }
        const unsupported = isUnsupportedDestination(payload);
        logger.info('agent.navigation.rejected', {
          reason: unsupported ? 'unsupported_destination' : 'invalid_action',
        });
        message.error(before.t(unsupported ? 'agent.unsupportedDestination' : 'agent.openFailed'));
        return false;
      }
      try {
        const { mainChildRoutes } = await import('services/config/router');
        const now = current.current;
        if (
          !now.allowed ||
          (!now.fullPage && !now.mode) ||
          activeService.current?.getSnapshot().selectedId !== sessionId ||
          (panel &&
            (now.pathname !== before.pathname ||
              now.fullPage !== before.fullPage ||
              now.mode !== before.mode))
        )
          return false;
        const path = AGENT_PAGES[action.workspace_id];
        if (
          !path ||
          (action.workspace_id !== 'agent' && !canNavigate(path, mainChildRoutes, now.access))
        ) {
          logger.info('agent.navigation.denied', { target: action.workspace_id });
          message.error(now.t('agent.permissionDenied'));
          return false;
        }
        logger.info('agent.navigation', { target: action.workspace_id });
        if (panel) {
          if (now.fullPage) {
            if (now.activePanel) closePanel(sessionId);
            logger.info('agent.navigation.known_panel', { target: panel.target });
            return knownPanelExecutor.current?.(panel, payload, path, sessionId) ?? false;
          }
          if (now.activePanel) {
            const replace = await new Promise<boolean>((resolve) => {
              Modal.confirm({
                title: before.t('agent.panelReplace.title'),
                content: before.t('agent.panelReplace.description'),
                okText: before.t('agent.panelReplace.confirm'),
                cancelText: before.t('agent.cancel'),
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
              });
            });
            if (!replace) {
              logger.info('agent.panel.kept', {
                target: now.activePanel.target,
                reason: 'declined',
              });
              return false;
            }
            closePanel(sessionId);
            const latest = current.current;
            if (
              !latest.allowed ||
              (!latest.fullPage && !latest.mode) ||
              activeService.current?.getSnapshot().selectedId !== sessionId ||
              latest.pathname !== before.pathname ||
              latest.fullPage !== before.fullPage ||
              latest.mode !== before.mode
            )
              return false;
          }
          const storeAuth = parseStoreAuth(payload);
          if (storeAuth && !storeAuth.platform) {
            setPlatformChoices((current) => ({
              ...current,
              [sessionId]: { payload, sessionId, path: now.pathname },
            }));
            logger.info('agent.store_platform.requested');
            return false;
          }
          setPlatformChoices((current) => removePendingPanelAction(current, sessionId));
          const destination = storeAuth?.platform
            ? `${path}?tabName=${storeAuth.platform.toUpperCase()}`
            : `${path}${panel.navigationSearch || ''}`;
          if (
            !navigationArrived(now.pathname, now.search, destination) &&
            !(await knownNavigationExecutor.current?.(destination, sessionId))
          ) {
            logger.info('agent.panel.open_failed', {
              target: panel.target,
              reason: 'navigation',
            });
            return false;
          }
          if (panel.entrySelector) {
            const opened = await panelEntryExecutor.current?.(
              panel.entrySelector,
              () => openPanel(panel, false, path, sessionId),
              sessionId,
            );
            if (!opened) {
              logger.info('agent.panel.open_failed', {
                target: panel.target,
                reason: 'entry_unavailable',
              });
              return false;
            }
            logger.info('agent.panel.opened', { target: panel.target });
            return true;
          }
          openPanel(panel, false, path, sessionId);
          logger.info('agent.panel.opened', { target: panel.target });
          return true;
        }
        const destination = navigationPath(action);
        const arrived = await knownNavigationExecutor.current?.(destination, sessionId) ?? false;
        if (arrived) {
          navigationDestinations.current.set(sessionId, { actionId: action.id, path: destination });
          logger.info('agent.navigation.opened', { target: action.workspace_id });
        }
        return arrived;
      } catch {
        agentInitiatedNavigation.current = false;
        logger.info('agent.navigation.failed', { target: action.workspace_id });
        message.error(current.current.t('agent.openFailed'));
        return false;
      }
    },
    [openPanel, closePanel],
  );
  useEffect(() => {
    if (!allowed || !key) {
      closePanel();
      setPlatformChoices({});
      deferredNavigation.current.clear();
      stayOnHome.current = false;
      agentInitiatedNavigation.current = false;
      setService(undefined);
      setMode(null);
      setReturnSource(null);
      return;
    }
    const api = createAgentApi({
      host: getLeYanBotHost,
      async headers() {
        const token = current.current.auth.token;
        const identity = current.current.auth.payload;
        if (!token || !current.current.allowed || `${identity?.orgId}:${identity?.id}` !== key)
          throw new Error('Agent unavailable');
        return { Authorization: `Bearer ${token}`, 'X-Device-Id': await getUnionId() };
      },
      onResponse(response) {
        const identity = current.current.auth.payload;
        if (!current.current.allowed || `${identity?.orgId}:${identity?.id}` !== key) return;
        const token = response.headers.get('new-jwt');
        if (token)
          authorizationEvents.emit('authorize', token, decode(token, { json: true }), true);
        if (response.status === 401) logout('unAuthorize');
        if (response.status === 460) logout('stopService');
      },
      log: logger.info,
    });
    const pointer = createPagePointer(document, trackPageTask);
    setPointerSnapshot(null);
    const unsubscribePointer = pointer.subscribe(() => setPointerSnapshot(pointer.getSnapshot()));
    const runner = createAgentPageTask({
      pointer,
      ready: async () => current.current.allowed && current.current.pageVisible,
      track: trackPageTask,
    });
    let next: AgentService;
    const pointPanelEntry = async (
      selector: string,
      activate: (entry: HTMLElement) => void | Promise<void>,
      id: string,
    ) => {
      const deadline = Date.now() + 3000;
      while (Date.now() < deadline && next.getSnapshot().selectedId === id) {
        const entry = Array.from(document.querySelectorAll<HTMLElement>(selector))
          .find((element) => element.getClientRects().length > 0);
        if (entry) return pointer.port.point(entry, () => activate(entry));
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      return false;
    };
    panelEntryExecutor.current = pointPanelEntry;
    const executePageTask = async (
      task: AgentPageTask, id: string, fromNavigation = false,
    ): Promise<boolean> => {
      const now = current.current;
      if (
        !now.allowed || !now.pageVisible ||
        (!now.fullPage && !now.mode) ||
        next.getSnapshot().selectedId !== id
      ) {
        logger.info('agent.page_task.rejected', { reason: 'surface_unavailable' });
        if (next.getSnapshot().selectedId === id) {
          next.startPageTask(id, task.id);
          next.finishPageTask({
            actionId: task.id,
            status: 'blocked',
            reason: 'execution_failed',
            completedSteps: 0,
            modelCalls: 0,
            durationMs: 0,
          });
        }
        return false;
      }
      const abort = new AbortController();
      pageTaskAbort.current?.abort();
      pageTaskAbort.current = abort;
      next.startPageTask(id, task.id);
      const scope = fromNavigation ? 'workspace'
        : now.fullPage && now.activePanel ? 'form' : task.handoff?.scope ?? 'workspace';
      if (now.fullPage && scope === 'workspace') setMode('sidebar');
      const destination = navigationDestinations.current.get(id);
      if (destination && task.handoff && task.handoff.facts.length < 20 && task.afterActionId === destination.actionId) {
        task = { ...task, handoff: { ...task.handoff, facts: [
          ...task.handoff.facts,
          `The requested workspace ${destination.path.split('?')[0]} has already been opened by clicking its tab/menu. Continue the remaining task there; do not repeat navigation or switch business modules just because a record mentions a shop.`,
        ] } };
      }
      logger.info('agent.page_task.started', { taskId: task.id, scope });
      try {
        const outcome = await runner.run(
          task,
          scope,
          (observation) => api.decidePageAction(id, agentPageTaskPrompt(task).goal, observation, abort.signal),
          abort.signal,
          (progress) => next.progressPageTask(progress),
        );
        const result = runner.getResult?.(task.id) ?? {
          actionId: task.id,
          status: outcome,
          reason: outcome === 'cancelled' ? 'interrupted' as const : 'execution_failed' as const,
          completedSteps: 0,
          modelCalls: 0,
          durationMs: 0,
        };
        next.finishPageTask(result);
        logger.info('agent.page_task.settled', {
          taskId: task.id,
          status: result.status,
          reason: result.reason,
          completedSteps: String(result.completedSteps),
          modelCalls: String(result.modelCalls),
          durationMs: String(result.durationMs),
        });
        return result.status === 'completed';
      } catch {
        logger.info('agent.page_task.failed', { taskId: task.id });
        next.finishPageTask({
          actionId: task.id,
          status: abort.signal.aborted ? 'cancelled' : 'failed',
          reason: abort.signal.aborted ? 'interrupted' : 'execution_failed',
          completedSteps: 0,
          modelCalls: 0,
          durationMs: 0,
        });
        return false;
      } finally {
        if (pageTaskAbort.current === abort) pageTaskAbort.current = undefined;
        if (now.fullPage && current.current.fullPage && scope === 'workspace') setMode(null);
      }
    };
    next = createAgentService(api, (payload, id) => navigate(payload, id), async (task, id) => {
      await executePageTask(task, id);
    });
    const restoredSessionId = selectionIdentity
      ? readAgentSelection(localStorage, selectionIdentity)
      : undefined;
    if (restoredSessionId) {
      next.select(restoredSessionId);
      logger.info('agent.conversation.restored', { source: 'last_viewed' });
    }
    const executeNavigationTask = (task: AgentPageTask, id: string) =>
      executePageTask(task, id, true);
    pageTaskExecutor.current = executeNavigationTask;
    const executeKnownNavigation = async (destination: string, id: string) => {
      agentInitiatedNavigation.current = true;
      try {
        return await navigateAgentPage(destination, {
          document, pointer,
          location: () => current.current,
          active: () => current.current.allowed && current.current.pageVisible &&
            Boolean(current.current.fullPage || current.current.mode) && next.getSnapshot().selectedId === id,
          track: (event, data) => logger.info(event, data),
        });
      } finally {
        agentInitiatedNavigation.current = false;
      }
    };
    knownNavigationExecutor.current = executeKnownNavigation;
    const executeKnownPanel = async (
      panel: NonNullable<ReturnType<typeof parsePanelAction>>,
      payload: unknown, path: string, id: string,
    ): Promise<boolean> => {
      const now = current.current;
      if (!now.fullPage || !now.allowed || !now.pageVisible || next.getSnapshot().selectedId !== id)
        return false;
      pageTaskAbort.current?.abort();
      const storeAuth = parseStoreAuth(payload);
      const nativePath = panel.target === 'store_auth'
        ? quickStoreAuthorizationPath(payload, path) : undefined;
      const destination = nativePath || (storeAuth?.platform
        ? `${path}?tabName=${storeAuth.platform.toUpperCase()}`
        : `${path}${panel.navigationSearch || ''}`);
      if (!(await executeKnownNavigation(destination, id))) {
        logger.info('agent.panel.open_failed', { target: panel.target, reason: 'navigation' });
        return false;
      }
      if (panel.entrySelector) {
        const opened = await pointPanelEntry(
          panel.entrySelector,
          () => openPanel(panel, false, path, id),
          id,
        );
        if (!opened) {
          logger.info('agent.panel.open_failed', {
            target: panel.target,
            reason: 'entry_unavailable',
          });
          return false;
        }
        logger.info('agent.panel.opened', { target: panel.target });
        return true;
      }
      if (nativePath) {
        const nativeDeadline = Date.now() + 3000;
        const requestedPlatform = storeAuth?.platform?.toUpperCase();
        let entry: HTMLElement | null = null;
        while (Date.now() < nativeDeadline && next.getSnapshot().selectedId === id) {
          const storePage = Array.from(document.querySelectorAll<HTMLElement>('[data-agent-store-ready]'))
            .find((page) => page.getClientRects().length > 0 &&
              (!requestedPlatform || page.dataset.agentStorePlatform === requestedPlatform));
          if (storePage) {
            entry = storePage.querySelector<HTMLElement>('button[data-agent-panel="store_auth"]');
            if (entry?.getClientRects().length) break;
            logger.info('agent.panel.open_failed', { target: panel.target, reason: 'native_entry_unavailable' });
            return false;
          }
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        if (entry?.getClientRects().length) {
          const previousDialogs = new Set(document.querySelectorAll('[role="dialog"]'));
          if (!(await pointer.port.point(entry, () => entry!.click()))) return false;
          const formDeadline = Date.now() + 5000;
          while (Date.now() < formDeadline && next.getSnapshot().selectedId === id) {
            if (Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]'))
              .some((dialog) => !previousDialogs.has(dialog) && dialog.getClientRects().length > 0)) {
              logger.info('agent.panel.native_opened', { target: panel.target });
              return true;
            }
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
          logger.info('agent.panel.open_failed', { target: panel.target, reason: 'native_form' });
          return false;
        }
        logger.info('agent.panel.open_failed', { target: panel.target, reason: 'store_page_unavailable' });
        return false;
      }
      openPanel(panel, false, path, id);
      logger.info('agent.panel.opened', { target: panel.target });
      return true;
    };
    knownPanelExecutor.current = executeKnownPanel;
    activeService.current = next;
    setService(next);
    logger.info('agent.started', { clientApp: 'fett' });
    const deferred = deferredNavigation.current;
    const destinations = navigationDestinations.current;
    return () => {
      pageTaskAbort.current?.abort();
      runner.cancel?.();
      unsubscribePointer();
      pointer.dispose();
      if (pageTaskExecutor.current === executeNavigationTask) pageTaskExecutor.current = undefined;
      if (knownNavigationExecutor.current === executeKnownNavigation) knownNavigationExecutor.current = undefined;
      destinations.clear();
      if (knownPanelExecutor.current === executeKnownPanel) knownPanelExecutor.current = undefined;
      if (panelEntryExecutor.current === pointPanelEntry) panelEntryExecutor.current = undefined;
      closePanel();
      next.dispose();
      activeService.current = undefined;
      setPlatformChoices({});
      deferred.clear();
      stayOnHome.current = false;
      agentInitiatedNavigation.current = false;
      logger.info('agent.disposed');
    };
  }, [allowed, key, navigate, openPanel, closePanel, pageReaders, selectionIdentity]);
  useEffect(() => {
    if (!fullPage && !mode) pageTaskAbort.current?.abort();
  }, [fullPage, mode]);
  useEffect(() => {
    if (!service) {
      setAgentStatus({ thinking: false, unread: false });
      setSelectedSessionId(undefined);
      return undefined;
    }
    let persistedSessionId = selectionIdentity
      ? readAgentSelection(localStorage, selectionIdentity)
      : undefined;
    const update = () => {
      const snapshot = service.getSnapshot();
      if (selectionIdentity && snapshot.selectedId !== persistedSessionId) {
        writeAgentSelection(localStorage, selectionIdentity, snapshot.selectedId);
        persistedSessionId = snapshot.selectedId;
      }
      const kinds = collectUnreadKinds(snapshot.sessions, snapshot.messages);
      const nextStatus = {
        thinking: Object.values(snapshot.runs).some(Boolean),
        unread:
          completedUnreadSessionId(kinds, fullPage || mode ? snapshot.selectedId : undefined) !==
          undefined,
      };
      setAgentStatus((previous) =>
        previous.thinking === nextStatus.thinking && previous.unread === nextStatus.unread
          ? previous
          : nextStatus,
      );
      setSelectedSessionId(snapshot.selectedId);
    };
    const unsubscribe = service.subscribe(update);
    update();
    return unsubscribe;
  }, [service, fullPage, mode, selectionIdentity]);
  useEffect(() => {
    if (pageTaskAbort.current && service?.getSnapshot().pageTask?.sessionId !== selectedSessionId)
      pageTaskAbort.current.abort();
  }, [service, selectedSessionId]);
  useEffect(() => {
    if (!service || !fullPage) return;
    const value = pathname.slice('/agent'.length).replace(/^\//, '');
    if (value && !/^[A-Za-z0-9_-]{1,128}$/.test(value)) {
      nav('/403', { replace: true });
      return;
    }
    if (value && service.getSnapshot().selectedId !== value) service.select(value);
  }, [service, pathname, fullPage, nav]);
  useEffect(() => {
    if (!service || !fullPage || pathname !== '/agent' || stayOnHome.current) return;
    const restoredSessionId = service.getSnapshot().selectedId;
    if (restoredSessionId) select(restoredSessionId);
    void service.refresh('');
    // select is declared immediately below; the effect intentionally runs only when the route/service changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, fullPage, pathname]);
  useEffect(() => {
    if (!service || (!fullPage && !mode)) return;
    const sessionId = service.getSnapshot().selectedId;
    if (!sessionId) return;
    const payload = deferredNavigation.current.get(sessionId);
    if (!payload) return;
    deferredNavigation.current.delete(sessionId);
    void navigate(payload, sessionId);
  }, [service, fullPage, mode, pathname, pageVisible, navigate]);
  const select = useCallback((id?: string) => {
    stayOnHome.current = id === undefined;
    if (activeService.current?.getSnapshot().selectedId !== id) {
      pageTaskAbort.current?.abort();
      activeService.current?.select(id);
    }
    const now = current.current;
    if (now.fullPage) {
      const path = id ? `/agent/${encodeURIComponent(id)}` : '/agent';
      if (now.activeKey) {
        now.updateTab(now.activeKey, { path, search: '' });
        logger.info('agent.tab.session.changed');
      } else now.nav(path, { replace: true });
    }
  }, []);
  useEffect(() => {
    if (previousPathname.current !== pathname && !pathname.startsWith('/agent')) {
      stayOnHome.current = false;
    }
    previousPathname.current = pathname;
  }, [key, pathname]);
  const chooseStorePlatform = useCallback(
    (platform: string) => {
      const pending = platformChoice;
      if (!pending) return;
      setPlatformChoices((current) => removePendingPanelAction(current, pending.sessionId));
      if (platform === 'cancel') {
        logger.info('agent.store_platform.cancelled');
        return;
      }
      const payload = withStoreAuthPlatform(pending.payload, platform);
      if (!payload) return;
      logger.info('agent.store_platform.selected', { platform });
      void navigate(payload, pending.sessionId);
    },
    [platformChoice, navigate],
  );
  const pageContext = useCallback(() => {
    const { pathname: path, search: currentSearch } = current.current;
    const workspace = workspaceForAgentLocation(path, currentSearch);
    const result = activeService.current?.getSnapshot().pageTask;
    return {
      ...pageReaders.read(path, workspace),
      ...((current.current.fullPage || current.current.mode)
        ? { pageTaskScope: current.current.fullPage && current.current.activePanel
          ? 'form' as const : 'workspace' as const }
        : {}),
      ...(result?.result && result.sessionId === activeService.current?.getSnapshot().selectedId
        ? { lastPageTaskResult: result.result }
        : {}),
    };
  }, [pageReaders]);
  const expand = useCallback(() => {
    const { pathname: path, search: sourceSearch, mode: sourceMode } = current.current;
    if (sourceMode === 'sidebar' || sourceMode === 'floating-window') {
      setReturnSource({ path, search: sourceSearch, mode: sourceMode });
      logger.info('agent.page.expanded', { source: sourceMode });
    }
    const id = activeService.current?.getSnapshot().selectedId;
    current.current.nav(id ? `/agent/${encodeURIComponent(id)}` : '/agent');
  }, []);
  const returnToSource = useCallback(() => {
    if (!returnSource) return;
    nav(`${returnSource.path}${returnSource.search}`);
    setMode(returnSource.mode);
    setReturnSource(null);
    logger.info('agent.page.returned', { surface: returnSource.mode });
  }, [nav, returnSource]);
  const context = useMemo<AgentContextValue | null>(
    () =>
      allowed && service
        ? {
            service,
            mode: fullPage ? 'full-page' : mode,
            setMode,
            fullPage,
            setPageContainer,
            setSidebarContainer,
            select,
            expand,
            returnToSource: fullPage && returnSource ? returnToSource : undefined,
            returnSourcePath: fullPage ? returnSource?.path : undefined,
            navigate,
            cancelPageTask: () => pageTaskAbort.current?.abort(),
            sidebarWidth,
            setSidebarWidth,
            pageContext,
            storePlatformOptions: platformChoice
              ? STORE_AUTH_PLATFORMS.filter(([platform]) =>
                  withStoreAuthPlatform(platformChoice.payload, platform),
                )
              : undefined,
            chooseStorePlatform,
            businessPanelOpen,
            businessContainer,
            setBusinessContainer,
          }
        : null,
    [
      allowed,
      service,
      mode,
      fullPage,
      select,
      expand,
      returnSource,
      returnToSource,
      navigate,
      sidebarWidth,
      pageContext,
      platformChoice,
      chooseStorePlatform,
      businessPanelOpen,
      businessContainer,
    ],
  );
  const floating = Boolean(context && !fullPage && mode === 'floating-window');
  const target = fullPage
    ? pageContainer
    : mode === 'sidebar'
    ? sidebarContainer
    : floating
    ? floatContainer
    : null;
  const panelContent = activePanel ? (
    <Suspense key={`${selectedSessionId}:${activePanel.id}`} fallback={<Spin />}>
      {activePanel.render({
        presentation: panelPlacement!,
        onClose: () => {
          closePanel(selectedSessionId);
          logger.info('agent.panel.closed', { target: activePanel.target });
        },
      })}
    </Suspense>
  ) : null;
  return (
    <AgentContext.Provider value={context}>
      <AgentPageReadersContext.Provider value={allowed ? pageReaders : null}>
        {children}
      </AgentPageReadersContext.Provider>
      {context && pointerSnapshot && (
        <div
          className="fett-agent-operation-pointer"
          aria-hidden="true"
          data-clicking={pointerSnapshot.clicking || undefined}
          style={{
            transform: `translate(${pointerSnapshot.x}px, ${pointerSnapshot.y}px)`,
            transition: pointerSnapshot.reducedMotion
              ? 'none'
              : `transform ${PAGE_POINTER_MOVE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          }}
        >
          {pointerSnapshot.clicking && <span className="fett-agent-operation-pointer-ripple" />}
          <MousePointer2 size={40} strokeWidth={1.5} />
        </div>
      )}
      {context &&
        activePanel &&
        (businessPanelOpen && businessContainer
          ? createPortal(
              <ConfigProvider
                getPopupContainer={() => businessContainer.parentElement ?? businessContainer}
                getTargetContainer={() => businessContainer.parentElement ?? businessContainer}
              >
                {panelContent}
              </ConfigProvider>,
              businessContainer,
            )
          : null)}
      {context && !fullPage && mode === null && (
        <Button
          className="fett-agent-launcher"
          type="primary"
          shape="circle"
          size="large"
          title={t('agent.title')}
          aria-label={t('agent.title')}
          icon={
            <span
              className={`fett-agent-launcher-visual ${agentStatus.thinking ? 'is-thinking' : ''}`}
            >
              <img
                className="fett-agent-launcher-icon"
                src={agentSegments}
                alt=""
                draggable={false}
              />
              {agentStatus.unread ? (
                <span
                  className="fett-agent-launcher-unread"
                  role="status"
                  aria-label={t('agent.unreadResult')}
                />
              ) : null}
            </span>
          }
          style={
            launcherPosition
              ? {
                  right: Math.max(0, Math.min(windowWidth - 48, launcherPosition.right)),
                  bottom: Math.max(0, Math.min(windowHeight - 48, launcherPosition.bottom)),
                }
              : undefined
          }
          onPointerDown={(event) => {
            if (event.button !== 0 || launcherDrag.current) return;
            const rect = event.currentTarget.getBoundingClientRect();
            suppressLauncherClick.current = false;
            launcherDrag.current = {
              pointerId: event.pointerId,
              x: event.clientX,
              y: event.clientY,
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const start = launcherDrag.current;
            if (!start || start.pointerId !== event.pointerId) return;
            const dx = event.clientX - start.x;
            const dy = event.clientY - start.y;
            if (!suppressLauncherClick.current && Math.hypot(dx, dy) < 4) return;
            suppressLauncherClick.current = true;
            setLauncherPosition({
              right: Math.max(
                0,
                Math.min(
                  window.innerWidth - start.width,
                  window.innerWidth - start.width - start.left - dx,
                ),
              ),
              bottom: Math.max(
                0,
                Math.min(
                  window.innerHeight - start.height,
                  window.innerHeight - start.height - start.top - dy,
                ),
              ),
            });
          }}
          onPointerUp={(event) => {
            if (launcherDrag.current?.pointerId !== event.pointerId) return;
            launcherDrag.current = undefined;
            if (event.currentTarget.hasPointerCapture(event.pointerId))
              event.currentTarget.releasePointerCapture(event.pointerId);
          }}
          onPointerCancel={() => {
            launcherDrag.current = undefined;
            suppressLauncherClick.current = true;
          }}
          onLostPointerCapture={() => {
            launcherDrag.current = undefined;
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') suppressLauncherClick.current = false;
          }}
          onClick={() => {
            if (suppressLauncherClick.current) {
              suppressLauncherClick.current = false;
              return;
            }
            setMode('floating-window');
          }}
        />
      )}
      {floating && (
        <section
          className="fett-agent-floating"
          onPointerDown={(event) => {
            if (
              event.button !== 0 ||
              !(event.target as HTMLElement).closest('.fett-agent-toolbar strong')
            )
              return;
            event.preventDefault();
            const rect = event.currentTarget.getBoundingClientRect();
            drag.current = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            if (!drag.current) return;
            const width = event.currentTarget.offsetWidth;
            setPosition({
              x: Math.max(
                0,
                Math.min(
                  window.innerWidth - width,
                  drag.current.left + event.clientX - drag.current.x,
                ),
              ),
              y: Math.max(
                0,
                Math.min(
                  window.innerHeight - event.currentTarget.offsetHeight,
                  drag.current.top + event.clientY - drag.current.y,
                ),
              ),
            });
          }}
          onPointerUp={() => {
            drag.current = undefined;
          }}
          onPointerCancel={() => {
            drag.current = undefined;
          }}
          role="dialog"
          aria-label={t('agent.title')}
          style={{
            ...floatSize,
            ...(position
              ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
              : {}),
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !event.defaultPrevented) setMode(null);
          }}
        >
          <div ref={setFloatContainer} className="fett-agent-floating-content" />
          {(['e', 's', 'se', 'w', 'n', 'nw', 'ne', 'sw'] as const).map((edge) => (
            <div
              key={edge}
              className={`fett-agent-resize fett-agent-resize-${edge}`}
              onPointerDown={(event) => {
                if (event.button !== 0) return;
                event.preventDefault();
                const rect = event.currentTarget.parentElement!.getBoundingClientRect();
                resize.current = {
                  x: event.clientX,
                  y: event.clientY,
                  width: rect.width,
                  height: rect.height,
                  left: rect.left,
                  top: rect.top,
                };
                setPosition({ x: rect.left, y: rect.top });
                event.currentTarget.setPointerCapture(event.pointerId);
              }}
              onPointerMove={(event) => {
                const start = resize.current;
                if (!start) return;
                const minWidth = Math.min(300, window.innerWidth - 16);
                const minHeight = Math.min(320, window.innerHeight - 16);
                const dx = event.clientX - start.x;
                const dy = event.clientY - start.y;
                const left = edge.includes('w')
                  ? Math.max(8, Math.min(start.left + start.width - minWidth, start.left + dx))
                  : start.left;
                const top = edge.includes('n')
                  ? Math.max(8, Math.min(start.top + start.height - minHeight, start.top + dy))
                  : start.top;
                const right = edge.includes('e')
                  ? Math.min(
                      window.innerWidth - 8,
                      Math.max(left + minWidth, start.left + start.width + dx),
                    )
                  : start.left + start.width;
                const bottom = edge.includes('s')
                  ? Math.min(
                      window.innerHeight - 8,
                      Math.max(top + minHeight, start.top + start.height + dy),
                    )
                  : start.top + start.height;
                setPosition({ x: left, y: top });
                setFloatSize({ width: right - left, height: bottom - top });
              }}
              onPointerUp={() => {
                resize.current = undefined;
              }}
              onPointerCancel={() => {
                resize.current = undefined;
              }}
              onLostPointerCapture={() => {
                resize.current = undefined;
              }}
            />
          ))}
        </section>
      )}
      {context &&
        target &&
        createPortal(
          <div
            data-agent-chat=""
            className={`fett-agent-workspace${
              fullPage && panelPlacement === 'sidebar' && activePanel ? ' has-panel' : ''
            }`}
          >
            <div className="fett-agent-workspace-chat">
              <Suspense fallback={<Spin />}>
                <ChatView />
              </Suspense>
            </div>
            {fullPage && panelPlacement === 'sidebar' && activePanel && (
              <aside className="fett-agent-workspace-panel">{panelContent}</aside>
            )}
          </div>,
          target,
        )}
    </AgentContext.Provider>
  );
}
